/**
 * ALLY SYSTEM — Space Force as a team.
 * Spec: docs/ALLIED-FORCES.md
 *
 * The player is the tip of a spear, not the last aircraft on Earth. Allied
 * units arrive at authored beats, fight, and leave — or die.
 *
 * THE RULE THAT KEEPS THIS FROM RUINING THE GAME: allies create MOMENTS, not
 * damage. They draw fire, clear a lane, and absorb a wave the player was not
 * expected to survive. They never out-damage the player, never touch a boss,
 * and never persist for a whole stage. Ally kills award the player 25% — enough
 * to feel good, never enough to farm.
 *
 * In fiction, every allied hull is a unit CONFIRMED CLEAN. The Lattice
 * propagates through firmware, so most of the arsenal is suspect; anything
 * that shows up to help has been verified. That is why they are rare, and why
 * losing one matters.
 */

import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

export type AllyKind = 'boat'

const MAX = 4

const SPEC = {
  boat: {
    url: '/models/thunderhead/ally-patrol-boat.glb',
    hp: 600,
    /** World units/sec travelling up the river lane. */
    speed: 2.6,
    /** Seconds on station before it turns for home. Long enough to matter. */
    dwell: 22,
    /** Twin mounts, firing hard. This is a warship, not a spotter. */
    fireIntervalMs: 420,
    burst: 4,
    bulletSpeed: 52,
    damage: 34,
    /** Engages most of the playfield — it should be clearing the screen. */
    range: 26,
    scale: 0.85,
    /** Seconds between missile salvos. */
    salvoIntervalMs: 2600,
    /** Missiles per salvo. A salvo is the point — a trickle is not. */
    salvoCount: 8,
    missileDamage: 70,
  },
} as const

interface Ally {
  kind: AllyKind
  x: number
  y: number
  hp: number
  age: number
  fireTimer: number
  salvoTimer: number
  alive: boolean
  node: THREE.Object3D | null
}

/** Allied tracer rounds — cool white-blue, never the enemy's orange. */
const MAX_TRACERS = 140
/** Allied missiles in flight. A salvo of 8 x up to 4 boats needs headroom. */
const MAX_MISSILES = 48

const MISSILE = {
  speed: 40,
  turnRate: 300,
  lifetime: 3.0,
  acquire: 26,
} as const

export class AllySystem {
  readonly group = new THREE.Group()

  private allies: Ally[] = []
  private models = new Map<AllyKind, THREE.Object3D>()

  // Tracer pool.
  private readonly tActive = new Uint8Array(MAX_TRACERS)
  private readonly tx = new Float32Array(MAX_TRACERS)
  private readonly ty = new Float32Array(MAX_TRACERS)
  private readonly tvx = new Float32Array(MAX_TRACERS)
  private readonly tvy = new Float32Array(MAX_TRACERS)
  private readonly tLife = new Float32Array(MAX_TRACERS)
  // Allied missile pool — same arc-then-seek behaviour as the player's, in
  // allied colours, so a friendly salvo reads instantly as ours.
  private readonly mActive = new Uint8Array(MAX_MISSILES)
  private readonly mx = new Float32Array(MAX_MISSILES)
  private readonly my = new Float32Array(MAX_MISSILES)
  private readonly mvx = new Float32Array(MAX_MISSILES)
  private readonly mvy = new Float32Array(MAX_MISSILES)
  private readonly mLife = new Float32Array(MAX_MISSILES)
  private readonly mTarget = new Int16Array(MAX_MISSILES)
  private readonly missileMesh: THREE.InstancedMesh
  private readonly missileTrail: THREE.InstancedMesh
  private readonly tracerMesh: THREE.InstancedMesh
  private readonly dummy = new THREE.Object3D()

  onEvent: ((kind: 'arrive' | 'kill' | 'lost' | 'resupply' | 'salvo', x: number, y: number) => void) | null = null
  /** Set once per ally so it resupplies exactly once. */
  private resupplied = new WeakSet<object>()

  constructor() {
    this.group.name = 'AlliedForces'
    const geo = new THREE.CapsuleGeometry(0.07, 0.4, 2, 5)
    const mat = new THREE.MeshBasicMaterial({
      color: '#cfe8ff',
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
    this.tracerMesh = new THREE.InstancedMesh(geo, mat, MAX_TRACERS)
    this.tracerMesh.frustumCulled = false
    this.tracerMesh.count = 0
    this.tracerMesh.renderOrder = 21
    this.group.add(this.tracerMesh)

    const mBody = new THREE.CapsuleGeometry(0.085, 0.4, 3, 6)
    const mMat = new THREE.MeshBasicMaterial({
      color: '#eaf6ff', toneMapped: false, depthWrite: false,
    })
    this.missileMesh = new THREE.InstancedMesh(mBody, mMat, MAX_MISSILES)
    this.missileMesh.frustumCulled = false
    this.missileMesh.count = 0
    this.missileMesh.renderOrder = 22
    this.group.add(this.missileMesh)

    const plume = new THREE.CircleGeometry(0.26, 8)
    const plumeMat = new THREE.MeshBasicMaterial({
      color: '#6fb8ff', transparent: true, opacity: 0.75,
      blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
    })
    this.missileTrail = new THREE.InstancedMesh(plume, plumeMat, MAX_MISSILES)
    this.missileTrail.frustumCulled = false
    this.missileTrail.count = 0
    this.missileTrail.renderOrder = 21
    this.group.add(this.missileTrail)
  }

  async load(): Promise<void> {
    const loader = new GLTFLoader()
    for (const [kind, spec] of Object.entries(SPEC) as [AllyKind, typeof SPEC.boat][]) {
      try {
        const gltf = await loader.loadAsync(spec.url)
        const src = gltf.scene
        src.rotation.x = Math.PI / 2
        src.scale.setScalar(spec.scale)
        this.models.set(kind, src)
      } catch {
        /* a missing ally must never break the stage */
      }
    }
  }

  /** Bring an ally on station. Authored — called by the stage director. */
  deploy(kind: AllyKind, x: number, y: number): void {
    if (this.allies.filter((a) => a.alive).length >= MAX) return
    const template = this.models.get(kind)
    const node = template ? template.clone(true) : null
    if (node) {
      node.position.set(x, y, -2.2)
      this.group.add(node)
    }
    this.allies.push({
      kind, x, y,
      hp: SPEC[kind].hp,
      age: 0,
      fireTimer: 0.6,
      salvoTimer: 1.2,
      alive: true,
      node,
    })
    this.onEvent?.('arrive', x, y)
  }

  update(
    dt: number,
    enemyCount: number,
    eActive: Uint8Array,
    ex: Float32Array,
    ey: Float32Array,
    onEnemyHit: (index: number, x: number, y: number) => void,
  ): void {
    const spec = SPEC.boat

    for (const a of this.allies) {
      if (!a.alive) continue
      a.age += dt

      // Runs up the river lane, then turns for home after its dwell.
      // RESUPPLY. Halfway through its station time the boat puts ordnance up
      // to the player. Friendly support is a chance to get STRONGER, not just
      // a chance to breathe — which is what makes finding one feel like relief
      // rather than a brief pause.
      if (a.age > spec.dwell * 0.45 && !this.resupplied.has(a)) {
        this.resupplied.add(a)
        this.onEvent?.('resupply', a.x, a.y + 2)
      }

      const outbound = a.age > spec.dwell
      a.y += (outbound ? -spec.speed * 2.4 : spec.speed) * dt
      if (a.node) a.node.position.set(a.x, a.y, -2.2)
      if (a.y < -30 || a.y > 34) {
        a.alive = false
        if (a.node) this.group.remove(a.node)
        continue
      }

      if (outbound) continue

      // SALVO. Eight missiles ripple off both mounts, arc outward, then turn
      // in. This is the show of force — everything at once, then reload.
      a.salvoTimer -= dt
      if (a.salvoTimer <= 0) {
        a.salvoTimer = spec.salvoIntervalMs / 1000
        for (let k = 0; k < spec.salvoCount; k += 1) {
          const side = k % 2 === 0 ? -1 : 1
          this.launchMissile(a.x + side * 0.5, a.y + 1.4, side, k)
        }
        this.onEvent?.('salvo', a.x, a.y)
      }

      a.fireTimer -= dt
      if (a.fireTimer > 0) continue
      a.fireTimer = spec.fireIntervalMs / 1000

      // Engage the nearest airborne target in range.
      let best = -1
      let bestD = spec.range * spec.range
      for (let e = 0; e < enemyCount; e += 1) {
        if (!eActive[e]) continue
        const dx = ex[e] - a.x
        const dy = ey[e] - a.y
        const d2 = dx * dx + dy * dy
        if (d2 < bestD) { bestD = d2; best = e }
      }
      if (best < 0) continue

      const dx = ex[best] - a.x
      const dy = ey[best] - a.y
      const d = Math.hypot(dx, dy) || 1
      for (let k = 0; k < spec.burst; k += 1) {
        // Slight spread so a burst reads as a burst, not a laser.
        const jitter = (k - (spec.burst - 1) / 2) * 0.05
        this.spawnTracer(
          a.x, a.y + 1.2,
          ((dx / d) + jitter) * spec.bulletSpeed,
          ((dy / d)) * spec.bulletSpeed,
        )
      }
    }
    this.allies = this.allies.filter((a) => a.alive || a.node)

    // ── Allied missiles ────────────────────────────────────────────────
    let mn = 0
    for (let i = 0; i < MAX_MISSILES; i += 1) {
      if (!this.mActive[i]) continue
      this.mLife[i] -= dt
      if (this.mLife[i] <= 0) { this.mActive[i] = 0; continue }

      let t = this.mTarget[i]
      if (t < 0 || !eActive[t]) {
        let best = -1
        let bestD = MISSILE.acquire * MISSILE.acquire
        for (let e = 0; e < enemyCount; e += 1) {
          if (!eActive[e]) continue
          const dx = ex[e] - this.mx[i]
          const dy = ey[e] - this.my[i]
          const d2 = dx * dx + dy * dy
          if (d2 < bestD) { bestD = d2; best = e }
        }
        t = best
        this.mTarget[i] = t
      }

      if (t >= 0) {
        const dx = ex[t] - this.mx[i]
        const dy = ey[t] - this.my[i]
        const desired = Math.atan2(dy, dx)
        const current = Math.atan2(this.mvy[i], this.mvx[i])
        let diff = desired - current
        while (diff > Math.PI) diff -= Math.PI * 2
        while (diff < -Math.PI) diff += Math.PI * 2
        const maxTurn = (MISSILE.turnRate * Math.PI) / 180 * dt
        const a2 = current + Math.max(-maxTurn, Math.min(maxTurn, diff))
        this.mvx[i] = Math.cos(a2) * MISSILE.speed
        this.mvy[i] = Math.sin(a2) * MISSILE.speed
        if (dx * dx + dy * dy < 1.3 * 1.3) {
          onEnemyHit(t, ex[t], ey[t])
          this.mActive[i] = 0
          continue
        }
      }

      this.mx[i] += this.mvx[i] * dt
      this.my[i] += this.mvy[i] * dt
      if (this.my[i] > 32 || Math.abs(this.mx[i]) > 24) { this.mActive[i] = 0; continue }

      const ang = Math.atan2(this.mvy[i], this.mvx[i]) - Math.PI / 2
      this.dummy.position.set(this.mx[i], this.my[i], 0.32)
      this.dummy.rotation.set(0, 0, ang)
      this.dummy.scale.setScalar(1)
      this.dummy.updateMatrix()
      this.missileMesh.setMatrixAt(mn, this.dummy.matrix)
      const k = Math.max(0.35, this.mLife[i] / MISSILE.lifetime)
      this.dummy.position.set(
        this.mx[i] - Math.cos(ang + Math.PI / 2) * 0.38,
        this.my[i] - Math.sin(ang + Math.PI / 2) * 0.38,
        0.28,
      )
      this.dummy.scale.setScalar(k)
      this.dummy.updateMatrix()
      this.missileTrail.setMatrixAt(mn, this.dummy.matrix)
      mn += 1
    }
    this.missileMesh.count = mn
    this.missileTrail.count = mn
    this.missileMesh.instanceMatrix.needsUpdate = true
    this.missileTrail.instanceMatrix.needsUpdate = true

    // Advance tracers and resolve hits.
    let n = 0
    for (let i = 0; i < MAX_TRACERS; i += 1) {
      if (!this.tActive[i]) continue
      this.tLife[i] -= dt
      this.tx[i] += this.tvx[i] * dt
      this.ty[i] += this.tvy[i] * dt
      if (this.tLife[i] <= 0 || this.ty[i] > 30) { this.tActive[i] = 0; continue }

      for (let e = 0; e < enemyCount; e += 1) {
        if (!eActive[e]) continue
        const dx = ex[e] - this.tx[i]
        const dy = ey[e] - this.ty[i]
        if (dx * dx + dy * dy > 0.9 * 0.9) continue
        onEnemyHit(e, ex[e], ey[e])
        this.tActive[i] = 0
        break
      }
      if (!this.tActive[i]) continue

      const a = Math.atan2(this.tvy[i], this.tvx[i]) - Math.PI / 2
      this.dummy.position.set(this.tx[i], this.ty[i], 0.25)
      this.dummy.rotation.set(0, 0, a)
      this.dummy.updateMatrix()
      this.tracerMesh.setMatrixAt(n++, this.dummy.matrix)
    }
    this.tracerMesh.count = n
    this.tracerMesh.instanceMatrix.needsUpdate = true
  }

  /** Enemy fire can kill an ally. They stay dead — that is the point. */
  damageNearest(x: number, y: number, radius: number, amount: number): boolean {
    for (const a of this.allies) {
      if (!a.alive) continue
      const dx = a.x - x
      const dy = a.y - y
      if (dx * dx + dy * dy > radius * radius) continue
      a.hp -= amount
      if (a.hp <= 0) {
        a.alive = false
        if (a.node) this.group.remove(a.node)
        this.onEvent?.('lost', a.x, a.y)
        return true
      }
      return false
    }
    return false
  }

  /** Damage per ally tracer — used by the caller when resolving a hit. */
  get tracerDamage(): number {
    return SPEC.boat.damage
  }

  /** Damage one allied missile deals. */
  get missileDamage(): number {
    return SPEC.boat.missileDamage
  }

  private launchMissile(x: number, y: number, side: number, k: number): void {
    for (let i = 0; i < MAX_MISSILES; i += 1) {
      if (this.mActive[i]) continue
      this.mActive[i] = 1
      this.mx[i] = x
      this.my[i] = y
      // Fan the salvo outward before it turns in — the arc is what makes a
      // missile launch look like a missile launch.
      this.mvx[i] = side * (12 + k * 2.2)
      this.mvy[i] = 30
      this.mLife[i] = MISSILE.lifetime
      this.mTarget[i] = -1
      return
    }
  }

  private spawnTracer(x: number, y: number, vx: number, vy: number): void {
    for (let i = 0; i < MAX_TRACERS; i += 1) {
      if (this.tActive[i]) continue
      this.tActive[i] = 1
      this.tx[i] = x
      this.ty[i] = y
      this.tvx[i] = vx
      this.tvy[i] = vy
      this.tLife[i] = 1.4
      return
    }
  }

  reset(): void {
    for (const a of this.allies) if (a.node) this.group.remove(a.node)
    this.allies = []
    this.tActive.fill(0)
    this.mActive.fill(0)
    this.tracerMesh.count = 0
    this.missileMesh.count = 0
    this.missileTrail.count = 0
  }

  dispose(): void {
    this.tracerMesh.geometry.dispose()
    this.missileMesh.geometry.dispose()
    this.missileTrail.geometry.dispose()
    ;(this.tracerMesh.material as THREE.Material).dispose()
    ;(this.missileMesh.material as THREE.Material).dispose()
    ;(this.missileTrail.material as THREE.Material).dispose()
  }
}
